// multiple_choice — one prompt, up to 4 options, single answer. The payload
// arrives with `correctId` stripped: the renderer submits the chosen id and the
// server scores it. `cb.outcome` drives correct/incorrect styling once known.

import { useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ActivityImage, ArabicText, AudioButton, ChoiceButton } from './primitives';
import type { ActivityRendererProps } from './types';

// The stripped payload the client receives (no correctId).
type StrippedMcOption = { id: string; labelKey: string; image?: string | null };
type StrippedMc = {
  promptKey: string;
  promptAudio?: string | null;
  options: StrippedMcOption[];
};

export function MultipleChoice({ data, ctx, cb }: ActivityRendererProps<StrippedMc>): ReactElement {
  const { t } = ctx;
  const [chosen, setChosen] = useState<string | null>(null);

  function choose(id: string): void {
    if (cb.locked) return;
    setChosen(id);
    cb.onAnswer({ kind: 'choose', chosenId: id } satisfies content.AttemptInput);
  }

  return (
    <div className="mx-auto flex max-w-md flex-col">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-ink-900">
          <ArabicText text={t(data.promptKey)} ctx={ctx} />
        </h2>
        {data.promptAudio ? (
          <div className="mt-4">
            <AudioButton src={data.promptAudio} ctx={ctx} label={t('content.common.play_audio')} />
          </div>
        ) : null}
      </div>

      <div className="mt-7 grid gap-3">
        {data.options.map((opt) => {
          const isChosen = chosen === opt.id;
          const state =
            cb.locked && isChosen ? (cb.outcome?.correct ? 'correct' : 'incorrect') : 'idle';
          return (
            <ChoiceButton
              key={opt.id}
              onClick={() => choose(opt.id)}
              selected={isChosen}
              state={state}
              disabled={cb.locked}
            >
              {opt.image ? (
                <span className="h-12 w-12 shrink-0">
                  <ActivityImage
                    src={opt.image}
                    ctx={ctx}
                    alt={t(opt.labelKey)}
                    fallbackEmoji="❓"
                  />
                </span>
              ) : null}
              <ArabicText text={t(opt.labelKey)} ctx={ctx} />
            </ChoiceButton>
          );
        })}
      </div>
    </div>
  );
}
